import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, MessageCircle } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const faqs = [
  {
    question: 'What services does TECHNIFIRE Software offer?',
    answer: 'We offer seven core services: Website Development, Branding & Design, CRM Systems, E-Commerce, Landing Pages, SaaS Platforms, and AI Automation & Chatbots. Whether you need a simple landing page or a full-scale SaaS product, we have the expertise to deliver.',
  },
  {
    question: 'How much does a typical project cost?',
    answer: 'Project costs vary based on scope and complexity. Landing pages start around $3,500, custom websites range from $8,000–$25,000, e-commerce platforms from $12,000–$40,000, and SaaS platforms from $25,000+. We provide detailed proposals after understanding your requirements during a free consultation.',
  },
  {
    question: 'What is your development process?',
    answer: 'Our process follows five phases: (1) Discovery — understanding your goals, audience, and requirements; (2) Design — creating wireframes, UI/UX, and prototypes; (3) Development — building with agile sprints and regular demos; (4) Testing — quality assurance across devices and browsers; (5) Launch — deployment, training, and ongoing support.',
  },
  {
    question: 'How long does it take to build a website or app?',
    answer: 'Timelines depend on project scope. A landing page typically takes 2–3 weeks, a corporate website 4–8 weeks, an e-commerce platform 8–12 weeks, and a SaaS MVP 12–16 weeks. We provide detailed timelines during our initial consultation and keep you updated throughout.',
  },
  {
    question: 'Do you build AI chatbots and automation?',
    answer: 'Yes — AI automation is one of our core services. We build intelligent chatbots for websites, WhatsApp, and Slack using OpenAI GPT-4, LangChain, and custom NLP models. We also create workflow automations that can handle lead qualification, customer support, content generation, and data processing.',
  },
  {
    question: 'Can you help with branding and logo design?',
    answer: 'Absolutely. Our branding services include brand strategy, logo design, visual identity systems, brand guidelines, UI/UX design, and marketing collateral. We create cohesive brand experiences that resonate with your target audience and differentiate you from competitors.',
  },
  {
    question: 'Do you offer ongoing support and maintenance?',
    answer: 'Yes, we offer flexible support packages including website maintenance, security updates, performance monitoring, content updates, and technical support. Most clients choose our monthly retainer which includes priority support and a set number of development hours.',
  },
  {
    question: 'What technologies do you specialize in?',
    answer: 'Our tech stack includes React, Next.js, Vue.js, Laravel, Node.js, Python, PostgreSQL, Redis, Shopify, WordPress, OpenAI API, LangChain, and AWS. We choose the best technologies for your specific project requirements and scalability needs.',
  },
]

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.faq-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none none' }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="bg-[#050505] py-16 lg:py-24 px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
        {/* Left: Title + CTA Card */}
        <div className="lg:col-span-2 faq-animate">
          <span className="text-mono text-[#9A9A9A] mb-2 block">FAQ</span>
          <h2 className="text-h3 uppercase text-white mb-6">
            FREQUENTLY ASKED<br />QUESTIONS
          </h2>
          <p className="text-[#9A9A9A] mb-8">
            Got questions? We have answers. If you do not find what you are looking for, feel free to reach out.
          </p>

          {/* CTA Card */}
          <div className="bg-gradient-to-br from-[#B7000E] to-[#b7000f84] rounded-2xl p-8 border border-white/5">
            <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mb-5">
              <span className="text-2xl font-bold text-black">R</span>
            </div>
            <h3 className="text-xl text-white mb-2">Still have questions?</h3>
            <p className="text-sm text-[#9A9A9A] mb-6">
              Our team is ready to discuss your project and provide a free consultation.
            </p>
            <Link to="/contacts" className="btn-primary bg-white text-black hover:bg-[#B7000E] hover:text-white w-full justify-center">
              <MessageCircle className="w-4 h-4" />
              Let's talk
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Right: Accordion */}
        <div className="lg:col-span-3 space-y-0">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-animate border-t border-white/10">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between py-5 group cursor-pointer text-left"
              >
                <span className="text-white font-medium pr-4 group-hover:text-[#9A9A9A] transition-colors text-base">
                  {faq.question}
                </span>
                <span className={`text-xl text-[#9A9A9A] transition-all duration-300 shrink-0 ${openIndex === index ? 'rotate-45 text-white' : ''}`}>+</span>
              </button>
              <div className={`overflow-hidden transition-all duration-400 ease-out ${openIndex === index ? 'max-h-64 opacity-100 pb-5' : 'max-h-0 opacity-0'}`}>
                <p className="text-[#9A9A9A] leading-relaxed text-sm">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
